import React, { useEffect, useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { createUserWithEmailAndPassword, onAuthStateChanged, sendEmailVerification } from 'firebase/auth'
import { auth, db } from '../firebase'
import { ref, set, get } from 'firebase/database'
import Popup from './Popup'

// Modern minimal styles
const styles = {
  pageWrapper: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  container: {
    width: '100%',
    maxWidth: '680px',
  },
  card: {
    background: 'var(--card-bg, rgba(255, 255, 255, 0.95))',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: 'none',
    overflow: 'hidden',
  },
  cardBody: {
    padding: '40px',
  },
  logoWrapper: {
    width: '72px',
    height: '72px',
    background: 'rgba(129, 140, 248, 0.3)',
    borderRadius: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
  },
  logoIcon: {
    fontSize: '2rem',
    color: '#fff',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: 'var(--title-color, #1a1a2e)',
    marginBottom: '6px',
  },
  subtitle: {
    color: 'var(--subtitle-color, #6b7280)',
    fontSize: '0.9rem',
    marginBottom: '0',
  },
  formLabel: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--label-color, #374151)',
    marginBottom: '6px',
    display: 'block',
  },
  inputWrapper: {
    position: 'relative',
  },
  formControl: {
    height: '48px',
    borderRadius: '12px',
    border: '2px solid var(--input-border, #e5e7eb)',
    paddingLeft: '44px',
    fontSize: '0.95rem',
    transition: 'all 0.2s ease',
    backgroundColor: 'var(--input-bg, #f9fafb)',
    color: 'var(--input-color, #1a1a2e)',
  },
  inputIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--icon-color, #9ca3af)',
    fontSize: '1rem',
    zIndex: 4,
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '4px',
    zIndex: 4,
  },
  primaryBtn: {
    height: '50px',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    border: 'none',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    transition: 'all 0.3s ease',
  },
  linkBtn: {
    color: '#667eea',
    fontWeight: '600',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
  errorAlert: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '12px',
    color: '#dc2626',
    padding: '10px 14px',
    fontSize: '0.85rem',
  },
  roleCard: {
    border: '2px solid var(--role-card-border, #e5e7eb)',
    borderRadius: '16px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: 'var(--role-card-bg, #f9fafb)',
  },
  roleCardActive: {
    border: '2px solid #667eea',
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.15)',
  },
  infoBox: {
    background: 'var(--info-box-bg, linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%))',
    borderRadius: '12px',
    padding: '14px',
    marginTop: '16px',
    border: '1px solid var(--info-box-border, rgba(102, 126, 234, 0.2))',
  },
  selectControl: {
    height: '48px',
    borderRadius: '12px',
    border: '2px solid #e5e7eb',
    paddingLeft: '44px',
    fontSize: '0.95rem',
    transition: 'all 0.2s ease',
    backgroundColor: '#f9fafb',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '16px',
  },
}

function SignUp() {
  const [searchParams] = useSearchParams();
  const roleFromUrl = searchParams.get('role');
  
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [name, setName] = useState('')
  const [userType, setUserType] = useState(roleFromUrl || 'organizer')
  const [gameId, setGameId] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'info' })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const navigate = useNavigate()
  const [checkingAuth, setCheckingAuth] = useState(true)  
  useEffect(() => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user && user.emailVerified) {
          try {
            const userRef = ref(db, `users/${user.uid}`)
            const snapshot = await get(userRef)
  
            if (snapshot.exists()) {
              const userData = snapshot.val()
              const role = userData.role
  
              if (role === "organizer") {
                navigate("/organizer", { replace: true })
              } else if (role === "bidder") {
                navigate("/bidder", { replace: true })
              } else {
                navigate("/", { replace: true })
              }
            }
          } catch (err) {
            console.error("Error fetching user data:", err)
          }
        }
        setCheckingAuth(false)
      })
  
      return () => unsubscribe()
    }, [navigate])

  // Generate unique game ID
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError('Name/Organization name is required')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    // Validate game ID for bidders
    if (userType === 'bidder' && !gameId.trim()) {
      setError('Game ID is required for bidders')
      return
    }

    setLoading(true)

    try {
      // Verify game ID exists if user is a bidder
      if (userType === 'bidder') {
        const gameRef = ref(db, `games/${gameId}`)
        const gameSnapshot = await get(gameRef)

        if (!gameSnapshot.exists()) {
          setError('Invalid Game ID. Please check and try again.')
          setLoading(false)
          return
        }
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      )
      const user = userCredential.user
      const uid = user.uid

      const userData = {
        email: email,
        name: name,
        role: userType,
        createdAt: new Date().toISOString()
      }

      // Add gameId for bidders
      if (userType === 'bidder') {
        userData.gameId = gameId
      }

      await set(ref(db, `users/${uid}`), userData)

      // Send verification email
      try {
        await sendEmailVerification(user)
        setPopup({
          isOpen: true,
          title: 'Account Created',
          message: 'Account created successfully! A verification email has been sent to your inbox. Please verify your email before logging in.',
          type: 'success'
        })
      } catch (verErr) {
        console.error('Verification email error:', verErr)
        setPopup({
          isOpen: true,
          title: 'Account Created',
          message: 'Account created, but we could not send a verification email. Please check your email or contact support.',
          type: 'warning'
        })
      }

      // Give user a moment to read the popup, then navigate to login
      setTimeout(() => navigate('/login'), 1800)
    } catch (err) {
      // console.error('Sign up error:', err)

      // Handle specific error codes
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('This email is already registered.')
          break
        case 'auth/invalid-email':
          setError('Invalid email address.')
          break
        case 'auth/operation-not-allowed':
          setError('Email/password accounts are not enabled.')
          break
        case 'auth/weak-password':
          setError('Password is too weak. Use at least 6 characters.')
          break
        default:
          setError(err.message || 'Failed to create account. Please try again.')
      }
    } finally {
      auth.signOut()
      setLoading(false)
    }
  }

  if (checkingAuth) {
    return (
      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <div style={{ ...styles.card, padding: '60px', textAlign: 'center' }}>
            <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#667eea' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6b7280', marginBottom: 0 }}>Checking authentication...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.cardBody}>
            {/* Logo & Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              {/* <div style={styles.logoWrapper}>
                <i className="bi bi-person-plus-fill" style={styles.logoIcon}></i>
              </div> */}
              <h1 style={styles.title}>Create Account</h1>
              <p style={styles.subtitle}>Join SportsBid Arena today</p>
            </div>

            {/* Error Alert */}
            {error && (
              <div style={styles.errorAlert} className="d-flex align-items-center mb-3">
                <i className="bi bi-exclamation-circle-fill me-2"></i>
                <span style={{ flex: 1 }}>{error}</span>
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}
                  onClick={() => setError('')}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            )}

            {/* Role Selection Cards */}
            <div style={{ marginBottom: '20px' }}>
              <label style={styles.formLabel}>I want to join as</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div
                  style={{
                    ...styles.roleCard,
                    ...(userType === 'organizer' ? styles.roleCardActive : {}),
                  }}
                  onClick={() => !loading && setUserType('organizer')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: userType === 'organizer' ? '#ffc107' : '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <i className="bi bi-clipboard2-check" style={{ color: userType === 'organizer' ? '#fff' : '#6b7280', fontSize: '1.1rem' }}></i>
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--role-card-text, #1a1a2e)', fontSize: '0.95rem' }}>Organizer</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--role-card-subtext, #6b7280)' }}>Host auctions</div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    ...styles.roleCard,
                    ...(userType === 'bidder' ? styles.roleCardActive : {}),
                  }}
                  onClick={() => !loading && setUserType('bidder')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: userType === 'bidder' ? '#ffc107' : '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <i className="bi bi-person-raised-hand" style={{ color: userType === 'bidder' ? '#fff' : '#6b7280', fontSize: '1.1rem' }}></i>
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--role-card-text, #1a1a2e)', fontSize: '0.95rem' }}>Bidder</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--role-card-subtext, #6b7280)' }}>Join auctions</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Role Instructions */}
              <div style={styles.infoBox}>
                {userType === 'bidder' ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <i className="bi bi-info-circle-fill" style={{ color: '#667eea' }}></i>
                      <span style={{ fontWeight: '600', color: 'var(--role-card-text, #1a1a2e)' }}>Bidder Instructions</span>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--info-box-text, #4b5563)', lineHeight: '1.6' }}>
                      <li>Get the <strong>Game ID</strong> from your auction organizer</li>
                      <li>Join teams created by the organizer to participate</li>
                      <li>Place bids on players during live auctions</li>
                      <li>Track your team's budget and player roster</li>
                    </ul>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <i className="bi bi-info-circle-fill" style={{ color: '#667eea' }}></i>
                      <span style={{ fontWeight: '600', color: 'var(--role-card-text, #1a1a2e)' }}>Organizer Instructions</span>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--info-box-text, #4b5563)', lineHeight: '1.6' }}>
                      <li>Create auction games and share <strong>Game ID</strong> with bidders</li>
                      <li>Add players, teams, and configure auction settings</li>
                      <li>Control the auction flow and manage bids</li>
                      <li>Assign bidders to teams and track results</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Sign Up Form */}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '14px' }}>
                <label style={styles.formLabel}>
                  {userType === 'organizer' ? 'Organization Name' : 'Full Name'}
                </label>
                <div style={styles.inputWrapper}>
                  <i className="bi bi-person" style={styles.inputIcon}></i>
                  <input
                    type="text"
                    className="form-control"
                    style={styles.formControl}
                    placeholder={userType === 'organizer' ? 'Enter organization name' : 'Enter your full name'}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={styles.formLabel}>Email Address</label>
                <div style={styles.inputWrapper}>
                  <i className="bi bi-envelope" style={styles.inputIcon}></i>
                  <input
                    type="email"
                    className="form-control"
                    style={styles.formControl}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={styles.formLabel}>Password</label>
                  <div style={styles.inputWrapper}>
                    <i className="bi bi-lock" style={styles.inputIcon}></i>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-control"
                      style={{ ...styles.formControl, paddingRight: '40px' }}
                      placeholder="Min 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      style={styles.eyeButton}
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                </div>
                <div>
                  <label style={styles.formLabel}>Confirm Password</label>
                  <div style={styles.inputWrapper}>
                    <i className="bi bi-lock-fill" style={styles.inputIcon}></i>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="form-control"
                      style={{ ...styles.formControl, paddingRight: '40px' }}
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      style={styles.eyeButton}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading}
                    >
                      <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Game ID for Bidders */}
              {userType === 'bidder' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={styles.formLabel}>
                    Game ID <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <div style={styles.inputWrapper}>
                    <i className="bi bi-key" style={styles.inputIcon}></i>
                    <input
                      type="text"
                      className="form-control"
                      style={styles.formControl}
                      placeholder="Enter the game ID from organizer"
                      value={gameId}
                      onChange={(e) => setGameId(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Contact your organizer if you don't have a Game ID
                  </small>
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary w-100"
                style={styles.primaryBtn}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Creating Account...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Create Account
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0' }}>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
              <span style={{ padding: '0 16px', color: '#9ca3af', fontSize: '0.85rem' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
            </div>

            {/* Sign In Link */}
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#6b7280', marginBottom: 0, fontSize: '0.9rem' }}>
                Already have an account?{' '}
                <Link to="/sign-in" style={styles.linkBtn}>
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Popup
        isOpen={popup.isOpen}
        title={popup.title}
        message={popup.message}
        type={popup.type}
        onClose={() => setPopup({ ...popup, isOpen: false })}
      />

      <style>{`
        .form-control:focus {
          border-color: #667eea !important;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1) !important;
          background-color: #fff !important;
        }
        .btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5) !important;
        }
        
        /* Dark Mode Styles */
        [data-theme="dark"] .form-control:focus {
          background-color: #2d2d44 !important;
        }
        
        [data-theme="dark"] .form-control::placeholder {
          color: #808080 !important;
          opacity: 1;
        }
        
        [data-theme="dark"] .form-control {
          color: #f1f5f9 !important;
        }
      `}</style>
    </div>
  </>
  )
}

export default SignUp